/**
 * Sync Bus Event Types
 *
 * Shared type definitions for the unified SSE sync bus between rep and client.
 * All rep/client real-time communication flows through these event types.
 */

import type { RepSession } from './rep-session';
import type { ClientFlagType, PaymentOptionValue } from '@/lib/services/client-flags.service';

// ============================================================================
// Event Types
// ============================================================================

export type SyncEventType =
  // Bidirectional: either side can emit
  | 'session:status'
  | 'session:ended'
  | 'cobrowse:ready'
  | 'cobrowse:connected'
  | 'cobrowse:disconnected'
  | 'cobrowse:reconnecting'
  | 'heartbeat'
  // Server-push (periodic flag state refresh via SSE ping)
  | 'flags'
  // Rep -> Client only
  | 'unlock'
  | 'unlock-next-steps'
  | 'pricing:changed'
  | 'payment-option'
  | 'session:screen_share_requested'
  | 'client:soft-refresh'
  | 'client:auto-recovery-config'
  | 'client:progress-reset'
  // Client -> Rep only
  | 'progress';

export type SyncRole = 'rep' | 'client';

// ============================================================================
// Role-based command authorization
// ============================================================================

export const REP_ONLY_COMMANDS = new Set<SyncEventType>([
  'unlock',
  'unlock-next-steps',
  'pricing:changed',
  'payment-option',
  'session:screen_share_requested',
  'client:soft-refresh',
  'client:auto-recovery-config',
  'client:progress-reset',
]);

export const CLIENT_ONLY_COMMANDS = new Set<SyncEventType>([
  'progress',
]);

export const BIDIRECTIONAL_COMMANDS = new Set<SyncEventType>([
  'session:status',
  'session:ended',
  'heartbeat',
  'flags',
  'cobrowse:ready',
  'cobrowse:connected',
  'cobrowse:disconnected',
  'cobrowse:reconnecting',
]);

export function isAuthorized(role: SyncRole, type: SyncEventType): boolean {
  if (BIDIRECTIONAL_COMMANDS.has(type)) return true;
  if (role === 'rep') return REP_ONLY_COMMANDS.has(type);
  if (role === 'client') return CLIENT_ONLY_COMMANDS.has(type);
  return false;
}

// ============================================================================
// Event Payloads
// ============================================================================

export interface SessionStatusPayload {
  status: RepSession['status'];
  sessionId?: string;
  sessionToken?: string;
  repName?: string;
  screenShareRequestedAt?: string;
  consentAcceptedAt?: string;
}

export interface SessionEndedPayload {
  reason?: 'rep_ended' | 'client_ended' | 'expired' | 'timeout';
}

export interface CobrowseReadyPayload {
  code: string;
  sessionId: string;
  reconnectId?: string;
}

export interface CobrowseConnectedPayload {
  detectedBy: SyncRole;
  cobrowseSessionId?: string;
}

export interface CobrowseDisconnectedPayload {
  detectedBy: SyncRole;
  reason?: string;
}

export interface CobrowseReconnectingPayload {
  initiatedBy: SyncRole;
  reconnectId: string;
  attempt?: number;
}

export interface HeartbeatPayload {
  role: SyncRole;
  timestamp: number;
}

export interface UnlockPayload {
  flag: ClientFlagType;
  unlockId?: string;
  value?: string;
}

export interface UnlockNextStepsPayload {
  steps: Array<string>;
}

export interface PricingChangedPayload {
  changeType: 'line_items' | 'discount';
}

export interface PaymentOptionPayload {
  option: PaymentOptionValue | null;
}

export interface ScreenShareRequestedPayload {
  screenShareRequestedAt: string;
}

export interface ClientSoftRefreshPayload {
  mode: 'queries' | 'page';
  reason?: 'rep_initiated' | 'pricing_changed' | 'cache_cleared' | 'recovery';
}

export interface ClientAutoRecoveryConfigPayload {
  enabled?: boolean;
  cooldownMs?: number;
  changedBy?: string;
}

export interface ClientProgressResetPayload {
  resetAt: number;
}

export const AUTO_RECOVERY_COOLDOWN_MIN_MS = 500;
export const AUTO_RECOVERY_COOLDOWN_MAX_MS = 60_000;
export const AUTO_RECOVERY_COOLDOWN_STEP_MS = 500;
export const AUTO_RECOVERY_COOLDOWN_DEFAULT_MS = 30_000;
export const AUTO_RECOVERY_MAX_ATTEMPTS = 3;

/** Clamp to slider step range; NaN/non-finite → default. */
export function clampAutoRecoveryCooldownMs(input: unknown): number {
  if (typeof input !== 'number' || !Number.isFinite(input)) {
    return AUTO_RECOVERY_COOLDOWN_DEFAULT_MS;
  }
  const stepped =
    Math.round(input / AUTO_RECOVERY_COOLDOWN_STEP_MS) * AUTO_RECOVERY_COOLDOWN_STEP_MS;
  return Math.min(
    AUTO_RECOVERY_COOLDOWN_MAX_MS,
    Math.max(AUTO_RECOVERY_COOLDOWN_MIN_MS, stepped),
  );
}

export interface FlagsPayload {
  unlock: { isSet: boolean; unlockId?: string } | null;
  pricingChanged: boolean;
  paymentOption: string | null;
  clientStatedUnlock: boolean;
  autoRecoveryEnabled: boolean;
  autoRecoveryCooldownMs: number;
}

export interface ProgressPayload {
  step: number;
  route?: string;
  scene?: string;
  clientInstanceId?: string;
}

// ============================================================================
// Payload map (event type -> payload shape)
// ============================================================================

export interface SyncEventPayloadMap {
  'session:status': SessionStatusPayload;
  'session:ended': SessionEndedPayload;
  'cobrowse:ready': CobrowseReadyPayload;
  'cobrowse:connected': CobrowseConnectedPayload;
  'cobrowse:disconnected': CobrowseDisconnectedPayload;
  'cobrowse:reconnecting': CobrowseReconnectingPayload;
  'heartbeat': HeartbeatPayload;
  'flags': FlagsPayload;
  'unlock': UnlockPayload;
  'unlock-next-steps': UnlockNextStepsPayload;
  'pricing:changed': PricingChangedPayload;
  'payment-option': PaymentOptionPayload;
  'session:screen_share_requested': ScreenShareRequestedPayload;
  'client:soft-refresh': ClientSoftRefreshPayload;
  'client:auto-recovery-config': ClientAutoRecoveryConfigPayload;
  'client:progress-reset': ClientProgressResetPayload;
  'progress': ProgressPayload;
}

// ============================================================================
// Wire format (what goes over SSE / Redis Pub/Sub)
// ============================================================================

export interface SyncEvent<T extends SyncEventType = SyncEventType> {
  id: number;
  type: T;
  payload: SyncEventPayloadMap[T];
  sender: SyncRole;
  opportunityId: string;
  timestamp: number;
}

// ============================================================================
// State Snapshot (sent on SSE connect/reconnect)
// ============================================================================

export interface ClientProgress {
  step: number;
  route?: string;
  scene?: string;
  clientInstanceId?: string;
  updatedAt: string;
  paymentOption?: string;
  incomeSource?: string;
}

export interface SyncSnapshot {
  session: RepSession | null;
  progress: ClientProgress | null;
  flags: {
    unlock: { isSet: boolean; unlockId?: string } | null;
    pricingChanged: boolean;
    paymentOption: string | null;
    clientStatedUnlock: boolean;
    autoRecoveryEnabled: boolean;
    autoRecoveryCooldownMs: number;
  };
  cobrowse: {
    code: string | null;
    sessionId: string | null;
    isActive: boolean;
  };
  connections: {
    repConnected: boolean;
    clientConnected: boolean;
  };
}

// ============================================================================
// Command request (POST /api/sync/command body)
// ============================================================================

export interface SyncCommandRequest<T extends SyncEventType = SyncEventType> {
  opportunityId: string;
  type: T;
  payload: SyncEventPayloadMap[T];
  role: SyncRole;
}

// ============================================================================
// SSE connection state (client-side)
// ============================================================================

export type SyncConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'degraded';
